import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { DebugService } from './debug.service';

export type PerformanceMode = 'high' | 'medium' | 'low';

/**
 * Service to detect device performance capabilities and manage visual performance modes.
 * Automatically detects hardware limitations (CPU cores, memory, network) and adjusts
 * visual features like glass effects, shadows, animations, and blur radius.
 *
 * Performance Modes:
 * - HIGH: Full visual effects (8+ cores, 8+ GB RAM, 4g+ connection)
 * - MEDIUM: Reduced complexity (4-8 cores, 4-8 GB RAM, 3g connection)
 * - LOW: Minimal effects (≤4 cores, ≤4 GB RAM, 2g connection)
 */
@Injectable({ providedIn: 'root' })
export class PerformanceModeService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #debug = inject(DebugService);

  readonly #detectedMode = signal<PerformanceMode>('high');
  readonly #userOverride = signal<PerformanceMode | null>(null);

  /**
   * Current active performance mode (user override takes precedence over auto-detection)
   */
  readonly performanceMode = computed(() => this.#userOverride() ?? this.#detectedMode());

  /**
   * Enable glass morphism effects (backdrop-filter with blur)
   * Only enabled in HIGH performance mode
   */
  readonly enableGlassEffects = computed(() => this.performanceMode() === 'high');

  /**
   * Enable backdrop-filter CSS property
   * Disabled in LOW performance mode (too expensive for weak GPUs)
   */
  readonly enableBackdropFilter = computed(() => this.performanceMode() !== 'low');

  /**
   * Maximum blur radius in pixels for backdrop-filter and box-shadow effects
   * HIGH: 12px | MEDIUM: 6px | LOW: 0px
   */
  readonly maxBlurRadius = computed(() => {
    switch (this.performanceMode()) {
      case 'high':
        return 12;
      case 'medium':
        return 6;
      case 'low':
        return 0;
    }
  });

  /**
   * Enable CSS animations and transitions
   * Disabled in LOW performance mode to reduce GPU load
   */
  readonly enableAnimations = computed(() => this.performanceMode() !== 'low');

  /**
   * Enable background video playback
   * Disabled in LOW and MEDIUM modes (expensive decode + render)
   */
  readonly enableBackgroundVideo = computed(() => this.performanceMode() === 'high');

  /**
   * Reduce box-shadow complexity
   * TRUE in LOW/MEDIUM modes to use simplified shadows
   */
  readonly reduceBoxShadows = computed(() => this.performanceMode() !== 'high');

  constructor() {
    if (isPlatformBrowser(this.#platformId)) {
      this.#detectPerformanceMode();
      this.#loadUserPreference();
      this.#applyPerformanceStyles();
    }
  }

  /**
   * Auto-detect device performance based on hardware and network capabilities
   */
  #detectPerformanceMode(): void {
    const cores = navigator.hardwareConcurrency || 4;
    const memory = (navigator as any).deviceMemory || 4; // GB
    const connection = (navigator as any).connection;
    const effectiveType = connection?.effectiveType; // 'slow-2g', '2g', '3g', '4g'

    // LOW-END: Very weak devices
    if (cores <= 2 || memory <= 2 || effectiveType === 'slow-2g' || effectiveType === '2g') {
      this.#detectedMode.set('low');
    }
    // MEDIUM: Budget/older devices
    else if (cores <= 4 || memory <= 4 || effectiveType === '3g') {
      this.#detectedMode.set('medium');
    }
    // HIGH: Modern devices
    else {
      this.#detectedMode.set('high');
    }

    if (!this.#userOverride()) {
      this.#debug.logPerformanceMode('auto-detected', `Auto-detected: ${this.#detectedMode()}`, {
        cores,
        memory: `${memory}GB`,
        network: effectiveType || 'unknown',
      });
    }
  }

  /**
   * Load user's saved performance preference from localStorage
   */
  #loadUserPreference(): void {
    try {
      const saved = localStorage.getItem('performanceMode');
      if (saved && ['high', 'medium', 'low'].includes(saved)) {
        this.#userOverride.set(saved as PerformanceMode);
        this.#debug.logPerformanceMode('preference-loaded', `User preference loaded: ${saved}`);
      }
    } catch (error) {
      console.warn('[PerformanceMode] Failed to load user preference:', error);
    }
  }

  /**
   * Apply performance-based CSS variables and data attributes to document
   */
  #applyPerformanceStyles(): void {
    effect(() => {
      const mode = this.performanceMode();
      document.documentElement.setAttribute('data-performance-mode', mode);

      // Apply CSS custom properties based on performance mode
      const root = document.documentElement.style;

      // Blur variables
      if (this.enableBackdropFilter()) {
        root.setProperty('--blur', '0.6rem');
        root.setProperty('--strong-blur', `${this.maxBlurRadius()}px`);
      } else {
        root.setProperty('--blur', '0');
        root.setProperty('--strong-blur', '0');
      }

      // Animation speed
      if (!this.enableAnimations()) {
        root.setProperty('--transition-in-seconds', '0.01ms');
        root.setProperty('--transition-out-seconds', '0.01ms');
      } else {
        root.setProperty('--transition-in-seconds', '0.2s');
        root.setProperty('--transition-out-seconds', '0.15s');
      }

      this.#debug.logPerformanceMode('styles-applied', `Applied styles for mode: ${mode}`);
    });
  }

  /**
   * Manually set performance mode (overrides auto-detection)
   * @param mode - Performance mode to apply
   */
  setUserPreference(mode: PerformanceMode): void {
    this.#userOverride.set(mode);
    try {
      localStorage.setItem('performanceMode', mode);
      this.#debug.logPerformanceMode('preference-saved', `User preference saved: ${mode}`);
    } catch (error) {
      console.warn('[PerformanceMode] Failed to save user preference:', error);
    }
  }

  /**
   * Clear user preference and revert to auto-detection
   */
  clearUserPreference(): void {
    this.#userOverride.set(null);
    try {
      localStorage.removeItem('performanceMode');
      this.#debug.logPerformanceMode('preference-cleared', 'User preference cleared, using auto-detection');
    } catch (error) {
      console.warn('[PerformanceMode] Failed to clear user preference:', error);
    }
  }

  /**
   * Get detected hardware info for debugging/display
   */
  getDeviceInfo(): {
    cores: number;
    memory: number;
    connection: string;
    detectedMode: PerformanceMode;
    activeMode: PerformanceMode;
  } {
    return {
      cores: navigator.hardwareConcurrency || 0,
      memory: (navigator as any).deviceMemory || 0,
      connection: (navigator as any).connection?.effectiveType || 'unknown',
      detectedMode: this.#detectedMode(),
      activeMode: this.performanceMode(),
    };
  }
}
